import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultMember = {
  name: '',
  role: 'agent',
  title: '',
  bio: '',
  photo_url: '',
  email: '',
  phone: '',
  license_number: '',
  specialties: [],
  order: 0
};

export const AdminTeam = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState(defaultMember);
  const [specialtiesInput, setSpecialtiesInput] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/team?active_only=false`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Error fetching team');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        specialties: specialtiesInput.split(',').map(s => s.trim()).filter(Boolean)
      };

      if (editingMember) {
        await axios.put(`${API}/team/${editingMember.id}`, data);
        toast.success('Team member updated');
      } else {
        await axios.post(`${API}/team`, data);
        toast.success('Team member added');
      }
      setEditingMember(null);
      setFormData(defaultMember);
      setSpecialtiesInput('');
      fetchMembers();
    } catch (error) {
      toast.error('Error saving team member');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      await axios.delete(`${API}/team/${id}`);
      toast.success('Team member deleted');
      fetchMembers();
    } catch (error) {
      toast.error('Error deleting team member');
    }
  };

  const openEditModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData(member);
      setSpecialtiesInput(member.specialties?.join(', ') || '');
    } else {
      setEditingMember({});
      setFormData(defaultMember);
      setSpecialtiesInput('');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-team">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
              Team Members
            </h1>
            <p className="text-[#808080] mt-1">Manage your broker and agents</p>
          </div>
          <Button className="btn-gold" onClick={() => openEditModal()} data-testid="add-team-member-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Team Member
          </Button>
        </div>

        {/* Team Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : members.length === 0 ? (
          <div className="dashboard-card text-center py-16">
            <p className="text-[#808080]">No team members yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <div key={member.id} className="dashboard-card" data-testid={`team-member-card-${member.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] text-xl font-semibold">
                        {member.name.charAt(0)}
                      </div>
                    )}
                    <div className="ml-4">
                      <h3 className="text-[#F5F5F0] font-medium">{member.name}</h3>
                      <p className="text-[#D4AF37] text-sm">{member.title}</p>
                      <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                        member.role === 'broker' ? 'bg-purple-500/10 text-purple-400' : 'bg-blue-500/10 text-blue-400'
                      }`}>
                        {member.role}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => openEditModal(member)}
                      className="p-2 hover:bg-[#D4AF37]/10 rounded text-[#D4AF37]"
                      data-testid={`edit-member-${member.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(member.id)}
                      className="p-2 hover:bg-red-500/10 rounded text-red-400"
                      data-testid={`delete-member-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {member.bio && (
                  <p className="text-[#808080] text-sm mb-4 line-clamp-3">{member.bio}</p>
                )}
                
                <div className="text-sm space-y-1">
                  {member.email && <p className="text-[#808080]">{member.email}</p>}
                  {member.phone && <p className="text-[#808080]">{member.phone}</p>}
                </div>
                
                {!member.is_active && (
                  <span className="mt-3 inline-block text-xs px-2 py-1 bg-red-500/10 text-red-400 rounded">
                    Inactive
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Edit/Add Modal */}
        {editingMember !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="bg-[#111111] p-8 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
                  {editingMember.id ? 'Edit Team Member' : 'Add Team Member'}
                </h3>
                <button onClick={() => setEditingMember(null)} className="text-[#808080] hover:text-[#F5F5F0]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <Input
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-name-input"
                />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0]"
                  data-testid="member-role-select"
                >
                  <option value="broker">Broker</option>
                  <option value="agent">Agent</option>
                </select>
                <Input
                  placeholder="Title (e.g., Senior Agent)"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-title-input"
                />
                <Textarea
                  placeholder="Bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={3}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-bio-input"
                />
                <Input
                  placeholder="Photo URL"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-photo-input"
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-email-input"
                />
                <Input
                  placeholder="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-phone-input"
                />
                <Input
                  placeholder="License Number"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-license-input"
                />
                <Input
                  placeholder="Specialties (comma separated)"
                  value={specialtiesInput}
                  onChange={(e) => setSpecialtiesInput(e.target.value)}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-specialties-input"
                />
                <Input
                  type="number"
                  placeholder="Display Order"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
                  data-testid="member-order-input"
                />
                {editingMember.id && (
                  <label className="flex items-center text-[#F5F5F0]">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="mr-2"
                    />
                    Active
                  </label>
                )}
                <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-member-btn">
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
